import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Video,
  Save,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

export default function TwilioSettingsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testingTwilio, setTestingTwilio] = useState(false);
  const [twilioTestResult, setTwilioTestResult] = useState<any>(null);
  const [showTwilioDetails, setShowTwilioDetails] = useState(false);

  useEffect(() => {
    if (profile) {
      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      fetchSettings();
    }
  }, [profile, navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .in('setting_key', [
        'twilio_account_sid',
        'twilio_api_key',
        'twilio_api_secret'
      ]);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni',
        variant: 'destructive',
      });
    } else if (data) {
      const settingsMap: Record<string, string> = {};
      data.forEach((setting: any) => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      setSettings(settingsMap);
    }
    setLoading(false);
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);

    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('app_settings')
          .update({
            setting_value: value,
            updated_at: new Date().toISOString(),
            updated_by: profile?.id,
          })
          .eq('setting_key', key)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some settings');
      }

      toast({
        title: 'Impostazioni salvate',
        description: 'Le configurazioni Twilio sono state aggiornate',
      });

      setTwilioTestResult(null);
    } catch (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestTwilio = async () => {
    setTestingTwilio(true);
    setTwilioTestResult(null);

    try {
      const response = await fetch('/api/test/twilio', {
        method: 'POST',
      });

      const data = await response.json();

      setTwilioTestResult({
        success: data.success,
        message: data.message || data.error,
        details: data.details,
      });

      if (data.success) {
        toast({
          title: 'Test Twilio riuscito',
          description: data.message,
        });
      } else {
        toast({
          title: 'Test Twilio fallito',
          description: data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      setTwilioTestResult({
        success: false,
        message: 'Errore durante il test della connessione',
      });
      toast({
        title: 'Errore',
        description: 'Impossibile testare la connessione Twilio',
        variant: 'destructive',
      });
    } finally {
      setTestingTwilio(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/settings')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Video className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Configurazione Twilio</h1>
              <p className="text-gray-600 mt-1">Configura le videochiamate e le live session</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Credenziali API Twilio</CardTitle>
            <CardDescription>
              Configura le chiavi API per abilitare videochiamate e live streaming
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="twilio_account_sid">Account SID</Label>
              <div className="relative">
                <Input
                  id="twilio_account_sid"
                  type={showSecrets['twilio_account_sid'] ? 'text' : 'password'}
                  value={settings['twilio_account_sid'] || ''}
                  onChange={(e) => handleSettingChange('twilio_account_sid', e.target.value)}
                  placeholder="Inserisci l'Account SID di Twilio"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('twilio_account_sid')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['twilio_account_sid'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="twilio_api_key">API Key</Label>
              <div className="relative">
                <Input
                  id="twilio_api_key"
                  type={showSecrets['twilio_api_key'] ? 'text' : 'password'}
                  value={settings['twilio_api_key'] || ''}
                  onChange={(e) => handleSettingChange('twilio_api_key', e.target.value)}
                  placeholder="Inserisci l'API Key di Twilio"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('twilio_api_key')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['twilio_api_key'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="twilio_api_secret">API Secret</Label>
              <div className="relative">
                <Input
                  id="twilio_api_secret"
                  type={showSecrets['twilio_api_secret'] ? 'text' : 'password'}
                  value={settings['twilio_api_secret'] || ''}
                  onChange={(e) => handleSettingChange('twilio_api_secret', e.target.value)}
                  placeholder="Inserisci l'API Secret di Twilio"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => toggleShowSecret('twilio_api_secret')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecrets['twilio_api_secret'] ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <p className="text-xs text-gray-500">
                Ottieni le tue chiavi dalla{' '}
                <a
                  href="https://console.twilio.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Console Twilio
                </a>
              </p>
            </div>

            <Separator />

            <div className="space-y-3">
              <h4 className="text-sm font-medium">Test Connessione</h4>
              <Button
                type="button"
                onClick={handleTestTwilio}
                disabled={testingTwilio || !settings['twilio_account_sid'] || !settings['twilio_api_key'] || !settings['twilio_api_secret']}
                variant="outline"
                className="w-full"
              >
                {testingTwilio && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Testa Twilio
              </Button>

              {twilioTestResult && (
                <div className={`p-3 rounded-lg ${
                  twilioTestResult.success
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-start gap-2">
                    {twilioTestResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        twilioTestResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {twilioTestResult.success ? 'Connessione riuscita' : 'Connessione fallita'}
                      </p>
                      <p className={`text-xs mt-1 ${
                        twilioTestResult.success ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {twilioTestResult.message}
                      </p>
                    </div>
                  </div>

                  {twilioTestResult.details && !twilioTestResult.success && (
                    <Collapsible open={showTwilioDetails} onOpenChange={setShowTwilioDetails} className="mt-3">
                      <CollapsibleTrigger className="flex items-center gap-2 text-xs text-red-700 hover:text-red-800 font-medium">
                        <AlertCircle className="w-3 h-3" />
                        {showTwilioDetails ? 'Nascondi dettagli' : 'Mostra dettagli'}
                        {showTwilioDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-2">
                        <div className="bg-red-100 p-2 rounded text-xs font-mono text-red-900 overflow-auto max-h-40">
                          <pre className="whitespace-pre-wrap break-words">
                            {JSON.stringify(twilioTestResult.details, null, 2)}
                          </pre>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardHeader>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Come ottenere le credenziali</h3>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>Accedi alla <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Console Twilio</a></li>
                  <li>Copia l'Account SID dalla dashboard principale</li>
                  <li>Vai su Account → API keys & tokens</li>
                  <li>Crea una nuova API Key e salva Key SID e Secret</li>
                </ol>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            size="lg"
            className="min-w-[200px]"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
